import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import api, { isApiRequestCanceled } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";

const CustomizationContext = createContext(null);
const EMPTY_COLORS = { status_colors: {}, state_colors: {}, specialty_colors: {} };
const CUSTOMIZATION_TABLES = [
  ["business_tracker", "Business Tracker table"],
  ["revenue_details", "Revenue Details table"],
  ["services", "Services table"],
];
const CUSTOMIZATION_COLOR_GROUPS = [
  ["status_colors", "Status colors"],
  ["state_colors", "State colors"],
  ["specialty_colors", "Specialty colors"],
];
const isObjectRecord = (value) => (
  value && typeof value === "object" && !Array.isArray(value)
);
const asObject = (value) => (
  isObjectRecord(value) ? value : {}
);
const normalizeColors = (value) => {
  const colors = asObject(value);
  return {
    status_colors: asObject(colors.status_colors),
    state_colors: asObject(colors.state_colors),
    specialty_colors: asObject(colors.specialty_colors),
  };
};
const findIncompleteSettings = (configsPayload, colorsPayload) => {
  const configs = asObject(configsPayload);
  const issues = [];

  CUSTOMIZATION_TABLES.forEach(([key, label]) => {
    if (!Array.isArray(configs[key]?.columns)) {
      issues.push({ key, label });
    }
  });

  const summaryConfig = configs.revenue_summary?.config;
  if (
    !summaryConfig
    || typeof summaryConfig !== "object"
    || Array.isArray(summaryConfig)
    || !Array.isArray(summaryConfig.categories)
    || !Array.isArray(summaryConfig.metrics)
  ) {
    issues.push({ key: "revenue_summary", label: "Revenue Summary definitions" });
  }

  const colors = asObject(colorsPayload);
  CUSTOMIZATION_COLOR_GROUPS.forEach(([key, label]) => {
    if (!Object.prototype.hasOwnProperty.call(colors, key) || !isObjectRecord(colors[key])) {
      issues.push({ key, label });
    }
  });

  return issues;
};

const getWorkspaceHealth = async (workspaceId, signal) => {
  if (!workspaceId) return [];
  const response = await api.get("/customization-health", {
    params: { workspace_id: workspaceId },
    signal,
  });
  return Array.isArray(response.data?.issues) ? response.data.issues : [];
};

/**
 * Loads the workspace's Control Panel customization (table configs + colors)
 * once per workspace and shares it with every page.
 */
export const CustomizationProvider = ({ children }) => {
  const { user } = useAuth();
  const { current } = useWorkspace();
  const [customization, setCustomization] = useState({
    workspaceId: null,
    configs: null,
    colors: EMPTY_COLORS,
    incompleteSettings: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadVersionRef = useRef(0);
  const abortControllerRef = useRef(null);

  const load = useCallback(async (workspaceId) => {
    const loadVersion = ++loadVersionRef.current;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const [tc, cs] = await Promise.all([
        api.get("/table-configs", { signal: controller.signal }),
        api.get("/color-settings", { signal: controller.signal }),
      ]);
      // A previous workspace can finish after the current one. Only the
      // latest load may publish its response to the shared context.
      if (loadVersion === loadVersionRef.current && !controller.signal.aborted) {
        setCustomization({
          workspaceId,
          configs: asObject(tc.data),
          colors: normalizeColors(cs.data),
          incompleteSettings: findIncompleteSettings(tc.data, cs.data),
        });
      }
    } catch (e) {
      if (
        loadVersion === loadVersionRef.current &&
        !controller.signal.aborted &&
        !isApiRequestCanceled(e)
      ) {
        setError(e);
        throw e;
      }
    } finally {
      if (loadVersion === loadVersionRef.current) {
        abortControllerRef.current = null;
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    if (!current?.id) return Promise.resolve();
    return load(current.id);
  }, [current?.id, load]);
  const readWorkspaceHealth = useCallback(
    (workspaceId, signal) => getWorkspaceHealth(workspaceId, signal),
    []
  );

  useEffect(() => {
    if (!user || !current?.id) {
      loadVersionRef.current += 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
       setCustomization({
         workspaceId: null, configs: null, colors: EMPTY_COLORS, incompleteSettings: [],
       });
      setLoading(false);
      setError(null);
      return undefined;
    }

    // The workspace ID check below makes the old schema unavailable on the
    // render immediately following a switch, before this effect runs.
    // Clearing here also prevents it from being retained while loading.
    setCustomization({
      workspaceId: null, configs: null, colors: EMPTY_COLORS, incompleteSettings: [],
    });
    refresh().catch(() => {});

    // Invalidate an in-flight response before the next workspace effect runs.
    return () => {
      loadVersionRef.current += 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, [user, current?.id, refresh]);

  const configs = customization.workspaceId === current?.id ? customization.configs : null;
  const colors = customization.workspaceId === current?.id ? customization.colors : EMPTY_COLORS;

  // Normalized lookup maps for the color helpers
  const colorMaps = useMemo(() => {
    const state = {};
    Object.entries(colors.state_colors || {}).forEach(([k, v]) => { state[k.toUpperCase()] = v; });
    const specialty = {};
    Object.entries(colors.specialty_colors || {}).forEach(([k, v]) => { specialty[k.trim().toLowerCase()] = v; });
    return { status: colors.status_colors || {}, state, specialty };
  }, [colors]);

  const getColumns = useCallback(
    (tableKey) => {
      const columns = configs?.[tableKey]?.columns;
      return Array.isArray(columns) ? columns : [];
    },
    [configs]
  );
  const summaryLabels = asObject(configs?.revenue_summary?.labels);
  const sectionLabels = asObject(configs?.section_labels);

  return (
    <CustomizationContext.Provider value={{
      configs, colors, colorMaps, getColumns, summaryLabels, sectionLabels,
      incompleteSettings: customization.incompleteSettings,
      loading, error, refresh, getWorkspaceHealth: readWorkspaceHealth,
    }}>
      {children}
    </CustomizationContext.Provider>
  );
};

export const useCustomization = () => {
  const ctx = useContext(CustomizationContext);
  return ctx || {
    configs: null, colors: EMPTY_COLORS,
    colorMaps: { status: {}, state: {}, specialty: {} },
    getColumns: () => [], summaryLabels: {}, sectionLabels: {},
    incompleteSettings: [],
    loading: false, error: null, refresh: async () => {},
    getWorkspaceHealth: async () => [],
  };
};
